import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { PortfolioStats } from "./PortofolioStats";
import { PortfolioTable } from "./PortofolioTable";
import styles from "../account/AccountPage.module.css";
import { PortfolioFilter } from "./PortofolioFilter";
import TablePortoOpenOrders from "./TablePortoOpenOrders";
import TablePortoHistory from "./TablePortoHistory";
import { baseAxios } from "../../apis";

// --- Custom Hooks with Conditional Params ---

const usePortfolioOverviewQuery = (filters) => {
  const exchangeId = filters.exchange_id ?? "";
  const assetSearch = filters.asset_search ?? "";

  return useQuery({
    queryKey: ["portfolioOverview", exchangeId, assetSearch],
    queryFn: () => {
      const params = {
        exchange_id: exchangeId,
        asset_search: assetSearch,
      };

      return baseAxios
        .get("/api/portfolio/overview", { params })
        .then((res) => res.data);
    },
    keepPreviousData: true,
  });
};

const usePortfolioOpenOrdersQuery = (filters) => {
  const page = filters.page ?? 1;
  const exchangeId = filters.exchange_id ?? "";
  const assetSearch = filters.asset_search ?? "";

  return useQuery({
    queryKey: ["portfolioOpenOrders", page, exchangeId, assetSearch],
    queryFn: () => {
      const params = {
        page,
        exchange_id: exchangeId,
        asset_search: assetSearch,
      };

      return baseAxios
        .get("/api/portfolio/open_orders", { params })
        .then((res) => res.data);
    },
    keepPreviousData: true,
  });
};

const usePortfolioHistoryQuery = (filters) => {
  const page = filters.page ?? 1;
  const exchangeId = filters.exchange_id ?? "";
  const assetSearch = filters.asset_search ?? "";
  const botSearch = filters.bot_search ?? "";
  const sortBy = filters.sort_by ?? "";
  const sortDir = filters.sort_dir ?? "desc";

  return useQuery({
    queryKey: [
      "portfolioHistory",
      page,
      exchangeId,
      assetSearch,
      botSearch,
      sortBy,
      sortDir,
    ],
    queryFn: () => {
      const params = {
        page,
        exchange_id: exchangeId,
        asset_search: assetSearch,
        bot_search: botSearch,
        sort_by: sortBy,
        sort_dir: sortDir,
      };

      return baseAxios
        .get("/api/portfolio/history", { params })
        .then((res) => res.data);
    },
    keepPreviousData: true,
  });
};

export const PortfolioContent = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const [searchOverview, setSearchOverview] = useState("");
  const [selectedExchangeOverview, setSelectedExchangeOverview] =
    useState(null);

  const [pageOpenOrder, setPageOpenOrder] = useState(1);
  const [searchOpenOrder, setSearchOpenOrder] = useState("");
  const [selectedExchangeOpenOrder, setSelectedExchangeOpenOrder] =
    useState(null);

  const [pageHistory, setPageHistory] = useState(1);
  const [searchHistory, setSearchHistory] = useState("");
  const [selectedExchangeHistory, setSelectedExchangeHistory] = useState(null);
  const [selectedStrategyHistory, setSelectedStrategyHistory] = useState(null);
  const [sortFieldHistory, setSortFieldHistory] = useState(null);
  const [sortDirectionHistory, setSortDirectionHistory] = useState("desc");

  const [searchTextOverview, setSearchTextOverview] = useState("");
  const [searchTextOpenOrder, setSearchTextOpenOrder] = useState("");
  const [searchTextHistory, setSearchTextHistory] = useState("");

  const { data: overview, isLoading: loadingOverview } =
    usePortfolioOverviewQuery({
      asset_search: searchOverview,
      exchange_id: selectedExchangeOverview?.id,
    });

  const { data: openOrders, isLoading: loadingOpenOrders } =
    usePortfolioOpenOrdersQuery({
      page: pageOpenOrder,
      asset_search: searchOpenOrder,
      exchange_id: selectedExchangeOpenOrder?.id,
    });

  const { data: histories, isLoading: loadingHistories } =
    usePortfolioHistoryQuery({
      page: pageHistory,
      asset_search: searchHistory,
      exchange_id: selectedExchangeHistory?.id,
      bot_search: selectedStrategyHistory?.key,
      sort_by: sortFieldHistory,
      sort_dir: sortDirectionHistory,
    });

  const handleSearchOverview = (e) => {
    if (e.key === "Enter") setSearchOverview(searchTextOverview);
  };
  const handleSearchOpenOrder = (e) => {
    if (e.key === "Enter") setSearchOpenOrder(searchTextOpenOrder);
  };
  const handleSearchHistory = (e) => {
    if (e.key === "Enter") setSearchHistory(searchTextHistory);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className={styles.subMainContent}>
            <PortfolioFilter
              handleSearch={handleSearchOverview}
              searchText={searchTextOverview}
              setSearchText={setSearchTextOverview}
              setSelectedExchange={setSelectedExchangeOverview}
              selectedExchange={selectedExchangeOverview}
            />
            <div className="p-4 md:p-6 border border-[#DDE1E6] rounded-2xl ">
              <PortfolioStats overview={overview} loading={loadingOverview} />
              <br />
              <PortfolioTable
                overview={overview}
                loadingOverview={loadingOverview}
              />
            </div>
          </div>
        );
      case "openorders":
        return (
          <div className={styles.subMainContent}>
            <PortfolioFilter
              handleSearch={handleSearchOpenOrder}
              searchText={searchTextOpenOrder}
              setSearchText={setSearchTextOpenOrder}
              setSelectedExchange={setSelectedExchangeOpenOrder}
              selectedExchange={selectedExchangeOpenOrder}
            />
            <div className="p-4 md:p-6 border border-[#DDE1E6] rounded-2xl ">
              <PortfolioStats overview={overview} loading={loadingOverview} />
              <br />
              <TablePortoOpenOrders
                openOrders={openOrders}
                loadingOpenOrders={loadingOpenOrders}
                page={pageOpenOrder}
                setPage={setPageOpenOrder}
              />
            </div>
          </div>
        );
      case "history":
        return (
          <div className={styles.subMainContent}>
            <PortfolioFilter
              handleSearch={handleSearchHistory}
              searchText={searchTextHistory}
              setSearchText={setSearchTextHistory}
              setSelectedExchange={setSelectedExchangeHistory}
              setSelectedStrategy={setSelectedStrategyHistory}
              selectedExchange={selectedExchangeHistory}
              selectedStrategy={selectedStrategyHistory}
              isHistory
            />
            <TablePortoHistory
              loadingHistories={loadingHistories}
              histories={histories}
              setPage={setPageHistory}
              page={pageHistory}
              sortField={sortFieldHistory}
              sortDirection={sortDirectionHistory}
              setSortField={setSortFieldHistory}
              setSortDirection={setSortDirectionHistory}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div>
        <div className=" text-3xl font-bold capitalize mb-6">
          <div>Portfolio</div>
        </div>
      </div>

      <div className="bg-white p-4 md:p-6 rounded-xl">
        <div className="flex space-x-6 border-b border-gray-200">
          <span
            className={`pb-2 cursor-pointer text-sm font-medium ${
              activeTab === "overview"
                ? "text-[#1C1C1C] border-b-2 border-[#1C1C1C]"
                : "text-gray-400"
            }`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </span>
          <span
            className={`pb-2 cursor-pointer text-sm font-medium ${
              activeTab === "openorders"
                ? "text-[#1C1C1C] border-b-2 border-[#1C1C1C]"
                : "text-gray-400"
            }`}
            onClick={() => setActiveTab("openorders")}
          >
            Open Orders
          </span>
          <span
            className={`pb-2 cursor-pointer text-sm font-medium ${
              activeTab === "history"
                ? "text-[#1C1C1C] border-b-2 border-[#1C1C1C]"
                : "text-gray-400"
            }`}
            onClick={() => setActiveTab("history")}
          >
            History
          </span>
        </div>

        <div className="mt-6">{renderTabContent()}</div>
      </div>
    </div>
  );
};
